import React, { useContext, useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import Typography from 'kingpin/atoms/Typography';

import FlexCentered from 'components/Common/FlexCentered';
import VisualisationCacheContext from 'contexts/VisualisationCacheContext';
import useContentVisDefinitionIds from 'hooks/useContentVisDefinitionIds';
import { ContentInput } from 'hooks/useContentMetricIds';
import useGateLoadingFallback from 'hooks/useGateLoadingFallback';

import FleetOpsGrey from '../../images/fleetops-gray.svg';

type VisualisationsGateProps = {
  children: React.ReactNode;
} & Partial<ContentInput>;

const VisualisationsGate = ({
  children,
  ...contentInput
}: VisualisationsGateProps) => {
  const { dashboardGadgetIds, reportGadgetIds } = useContentVisDefinitionIds(
    (contentInput as ContentInput) ?? {
      content: undefined,
      contentType: undefined,
    },
  );
  const {
    requestDashboardGadgetsByIds,
    isGadgetIdResolved,
    isGadgetIdNotFound,
    requestReportGadgetsByIds,
    isReportGadgetIdResolved,
  } = useContext(VisualisationCacheContext);

  useEffect(() => {
    if (dashboardGadgetIds.length > 0) {
      requestDashboardGadgetsByIds(dashboardGadgetIds);
    }
  }, [dashboardGadgetIds, requestDashboardGadgetsByIds]);

  useEffect(() => {
    if (reportGadgetIds.length > 0) {
      requestReportGadgetsByIds(reportGadgetIds);
    }
  }, [reportGadgetIds, requestReportGadgetsByIds]);

  const dashboardResolving =
    dashboardGadgetIds.length > 0 &&
    !dashboardGadgetIds.every((id) => isGadgetIdResolved(id));
  const reportResolving =
    reportGadgetIds.length > 0 &&
    !reportGadgetIds.every((id) => isReportGadgetIdResolved(id));
  const isResolving = dashboardResolving || reportResolving;
  const { isLoading, fallback } = useGateLoadingFallback({
    contentInput,
    isResolving,
  });

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (reportGadgetIds.length > 0) {
    // All IDs resolved, but check if every single one is not-found
    // (total failure). If only some are missing, render children and
    // let individual card-level error boundaries handle the gaps.
    const allNotFound =
      dashboardGadgetIds.length > 0 &&
      dashboardGadgetIds.every((id) => isGadgetIdNotFound(id));
    if (allNotFound) {
      return (
        <FlexCentered style={{ height: '100%' }}>
          <ReactSVG
            src={FleetOpsGrey}
            style={{
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'center',
            }}
            beforeInjection={(svg) => {
              svg.setAttribute('style', 'width: 63px');
              svg.setAttribute('style', 'height: 72px');
            }}
          />
          <Typography.Body type="Body 14">
            Unable to load visualisations
          </Typography.Body>
        </FlexCentered>
      );
    }
  }

  return <>{children}</>;
};

export default VisualisationsGate;
